import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { House, User, Package, SignOut, Globe, CarProfile, ChatsTeardrop, Plus, Users } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useState, useEffect } from 'react';
import { getChatUnreadCount } from '../services/api';
import DemoBanner from '../components/DemoBanner';
import BrandLogo from '../components/BrandLogo';
import { useSystemInfo } from '../hooks/useSystemInfo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';

const ClientLayout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const { brand_name } = useSystemInfo();

  const navItems = [
    { path: '/portal', icon: House, label: t('nav.portal'), exact: true },
    { path: '/portal/orders', icon: Package, label: t('nav.myOrders') },
    { path: '/portal/new-order', icon: Plus, label: 'New Order' },
    { path: '/portal/end-clients', icon: Users, label: 'End Clients' },
    { path: '/portal/messages', icon: ChatsTeardrop, label: 'Messages' },
    { path: '/portal/profile', icon: User, label: t('nav.profile') },
  ];

  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let mounted = true;
    const fetchUnread = async () => {
      try { const r = await getChatUnreadCount(); if (mounted) setUnread(r.data?.unread || 0); } catch { /* ignore */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 8000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'ar', label: 'العربية' },
    { code: 'zh', label: '中文' }
  ];

  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-background" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      <DemoBanner />
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="h-16 flex items-center justify-between">
            {/* Logo */}
            <Link to="/portal" className="flex items-center gap-2">
              <BrandLogo size={36} alt={brand_name} />
              <span className="font-bold text-sm tracking-tight hidden sm:block">{brand_name}<br/><span className="text-[10px] font-medium text-muted-foreground">Order Manager</span></span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon weight="duotone" className="w-5 h-5" />
                  <span className="hidden sm:inline">{item.label}</span>
                  {item.path === '/portal/messages' && unread > 0 && (
                    <span className="absolute -top-1 -right-1 text-[10px] bg-red-600 text-white rounded-full px-1.5 py-0.5">{unread}</span>
                  )}
                </Link>
              ))}
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-3">
              {/* Language */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="portal-language-selector">
                    <Globe className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => i18n.changeLanguage(lang.code)}
                      className={i18n.language === lang.code ? 'bg-secondary' : ''}
                    >
                      {lang.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="portal-user-menu">
                    <div className="w-8 h-8 rounded-sm bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="portal-logout-btn">
                    <SignOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-border mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <CarProfile weight="duotone" className="w-5 h-5" />
              <span>{brand_name} Order Manager © 2026</span>
            </div>
            <div className="text-sm text-muted-foreground">
              Car Import Management Platform
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientLayout;
